import { type Account, type BalanceSummary, isZeroMoney } from '@familywallet/shared';
import { Banknote, CreditCard, Landmark, type LucideIcon, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormError } from '../components/FormError';
import { DebtsEntry } from '../components/DebtsEntry';
import { PageHeader } from '../components/PageHeader';
import { Button, buttonVariants } from '../components/ui/button';
import { HeroTotal } from '../components/HeroTotal';
import { Card, IconSquare, ListGroup, listRowClass, SectionLabel } from '../components/ui/card';
import { useMe } from '../lib/auth';
import { useAccounts, useBalanceSummary } from '../lib/ledger';
import { formatAmount } from '../lib/money';
import { BalanceLines, BaseEstimate } from '../components/Money';

/** Every currency of the account and, when some are not the base currency, "≈ ... at today's rate". */
export function AccountBalance({
  account,
  align = 'end',
}: {
  account: Account;
  align?: 'start' | 'end';
}) {
  const owes = account.kind === 'card';
  return (
    <span className={align === 'end' ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
      <BalanceLines
        balances={account.balances}
        owes={owes}
        className={align === 'end' ? 'flex flex-col items-end' : 'flex flex-col items-start'}
      />
      <BaseEstimate total={account.baseTotal} owes={owes} />
    </span>
  );
}

const KIND_ICONS: Record<Account['kind'], LucideIcon> = {
  cash: Banknote,
  bank: Landmark,
  wallet: Wallet,
  card: CreditCard,
};

/** The 40px icon square for an account kind. */
export function AccountIcon({ kind }: { kind: Account['kind'] }) {
  const Icon = KIND_ICONS[kind];
  return (
    <IconSquare>
      <Icon className="size-5" strokeWidth={2} />
    </IconSquare>
  );
}

function AccountRow({ account }: { account: Account }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link to={`/accounts/${account.id}`} className={listRowClass}>
        <AccountIcon kind={account.kind} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-medium">{account.name}</span>
          <span className="text-xs text-muted">
            {[
              t(`accountKinds.${account.kind}`),
              ...(account.isPrivate ? [t('accounts.private')] : []),
              ...(account.archivedAt ? [t('accounts.archived')] : []),
            ].join(' · ')}
          </span>
        </span>
        <span className="shrink-0 text-end text-[15px] font-medium">
          <AccountBalance account={account} />
        </span>
      </Link>
    </li>
  );
}

function SummaryCard({ summary }: { summary: BalanceSummary }) {
  const { t, i18n } = useTranslation();
  const money = (v: string) => formatAmount(v, summary.currency, i18n.language);
  const prefix = summary.estimated ? '≈ ' : '';
  return (
    <>
      <HeroTotal summary={summary} label={t('accounts.total')} />
      {(summary.members.length > 1 || !isZeroMoney(summary.shared)) && (
        <ListGroup>
          {summary.members.map((m) => (
            <li key={m.memberId} className="flex min-h-12 items-center justify-between gap-3 px-4">
              <span>{m.displayName}</span>
              <span className="font-medium tabular-nums" dir="ltr">
                {prefix}
                {money(m.total)}
              </span>
            </li>
          ))}
          <li className="flex min-h-12 items-center justify-between gap-3 px-4">
            <span>{t('accounts.sharedTotal')}</span>
            <span className="font-medium tabular-nums" dir="ltr">
              {prefix}
              {money(summary.shared)}
            </span>
          </li>
        </ListGroup>
      )}
    </>
  );
}

/** Accounts grouped: mine, shared family accounts, then each other member I can see. */
function groupAccounts(items: Account[], myId: string | undefined) {
  const groups = new Map<string, { title: string; items: Account[] }>();
  for (const account of items) {
    const key = account.ownerMemberId === myId ? 'mine' : (account.ownerMemberId ?? 'shared');
    const group = groups.get(key) ?? { title: key, items: [] };
    group.items.push(account);
    groups.set(key, group);
  }
  const order = (key: string) => (key === 'mine' ? 0 : key === 'shared' ? 1 : 2);
  return [...groups.entries()].sort(([a], [b]) => order(a) - order(b));
}

export function AccountsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [showArchived, setShowArchived] = useState(false);
  const accounts = useAccounts(showArchived);
  const summary = useBalanceSummary();

  const items = accounts.data?.items ?? [];
  const groups = groupAccounts(items, me.data?.member.id);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('accounts.title')}
        action={
          <Link to="/accounts/new" className={buttonVariants({ variant: 'small' })}>
            {t('accounts.add')}
          </Link>
        }
      />
      {summary.data && <SummaryCard summary={summary.data} />}
      {(accounts.isError || summary.isError) && (
        <FormError error={accounts.error ?? summary.error} />
      )}
      {accounts.isPending && <p role="status">{t('app.loading')}</p>}
      {accounts.isSuccess && items.length === 0 && (
        <Card>
          <p className="font-medium">{t('accounts.empty')}</p>
          <p className="text-sm text-muted">{t('accounts.emptyHint')}</p>
        </Card>
      )}
      {groups.map(([key, group]) => (
        <section key={key} className="flex flex-col gap-2">
          <SectionLabel>
            {key === 'mine'
              ? t('accounts.mine')
              : key === 'shared'
                ? t('accounts.shared')
                : t('accounts.ofMember', { name: group.items[0]?.ownerDisplayName ?? '' })}
          </SectionLabel>
          <ListGroup>
            {group.items.map((account) => (
              <AccountRow key={account.id} account={account} />
            ))}
          </ListGroup>
        </section>
      ))}
      <DebtsEntry />
      <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
        {showArchived ? t('accounts.hideArchived') : t('accounts.showArchived')}
      </Button>
    </div>
  );
}
